import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  beginAccessKeyHandoff,
  clearAccessKey,
  endAccessKeyHandoff,
  getAccessKey,
  holdAccessKeyHandoff,
  isAccessKeyHandoff,
  isAccessKeyHandoffHeld,
  onAccessKeyChange,
  setAccessKey,
} from "@/lib/api/client";
import { revokeCurrentSession } from "@/features/auth/api/auth.api";
import { cancelAllCeremonies } from "@/lib/webauthn";
import { clearStoredSupportContext } from "@/features/staff/api/staff.api";
import {
  clearPersistedRecoveryCodesAtRisk,
  rebindPersistedRecoveryCodesAtRisk,
  recoveryCodesAtRiskBindingMatches,
} from "@/features/account/recovery-codes-at-risk";
import { decideRecoveryCodesHandoff } from "@/providers/access-key-handoff";
import { passkeysKeys } from "@/features/account/hooks/usePasskeys";

const HANDOFF_STATUS_ID = "access-key-handoff-status";

interface AuthState {
  accessKey: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (accessKey: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [accessKey, setAccessKeyState] = useState<string | null>(getAccessKey);
  const [isLoading] = useState(false);
  const [handoff, setHandoff] = useState(false);

  const accessKeyRef = useRef(accessKey);
  const handoffGenerationRef = useRef(0);
  useEffect(() => {
    const root = document.getElementById("root");
    const dialogs = document.getElementById("dialogs");
    if (handoff) {
      root?.setAttribute("inert", "");
      dialogs?.setAttribute("inert", "");
    } else {
      root?.removeAttribute("inert");
      dialogs?.removeAttribute("inert");
    }
    return () => {
      root?.removeAttribute("inert");
      dialogs?.removeAttribute("inert");
    };
  }, [handoff]);
  useEffect(() => {
    if (!handoff) return;
    const region = document.getElementById(HANDOFF_STATUS_ID);
    if (region === null) return;
    const timer = window.setTimeout(() => {
      region.textContent = t("auth.handoff.checkingRecoveryCodes");
    }, 0);
    return () => {
      window.clearTimeout(timer);
      region.textContent = "";
    };
  }, [handoff, t]);
  const handoffAbortRef = useRef<AbortController | null>(null);
  const handoffRestartsRef = useRef(0);
  useEffect(() => {
    const finish = (
      generation: number,
      decision: "rebind" | "clear",
      previous: string,
      next: string | null,
    ) => {
      if (generation !== handoffGenerationRef.current) return;
      if (getAccessKey() !== next) {
        endAccessKeyHandoff();
        setHandoff(false);
        handoffRestartsRef.current = 0;
        void queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
        return;
      }
      const resolved =
        decision === "rebind" && recoveryCodesAtRiskBindingMatches(previous) ? "rebind" : "clear";
      holdAccessKeyHandoff();
      handoffRestartsRef.current = 0;
      if (resolved === "rebind" && next !== null)
        rebindPersistedRecoveryCodesAtRisk(previous, next);
      else if (next !== null || !recoveryCodesAtRiskBindingMatches(previous))
        clearPersistedRecoveryCodesAtRisk();
      window.location.reload();
    };

    const reloadIfKeyChanged = () => {
      if (isAccessKeyHandoffHeld()) return;
      const previous = accessKeyRef.current;
      const next = getAccessKey();
      if (next === previous) {
        if (!isAccessKeyHandoff()) return;
        handoffGenerationRef.current += 1;
        handoffAbortRef.current?.abort();
        endAccessKeyHandoff();
        setHandoff(false);
        handoffRestartsRef.current = 0;
        void queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
        return;
      }
      handoffGenerationRef.current += 1;
      const generation = handoffGenerationRef.current;
      handoffAbortRef.current?.abort();
      const abort = new AbortController();
      handoffAbortRef.current = abort;

      if (next === null || previous === null || !recoveryCodesAtRiskBindingMatches(previous)) {
        handoffRestartsRef.current = 0;
        finish(generation, "clear", previous ?? "", next);
        return;
      }

      if (isAccessKeyHandoff()) {
        handoffRestartsRef.current += 1;
        if (handoffRestartsRef.current >= 2) {
          finish(generation, "clear", previous, next);
          return;
        }
      } else {
        handoffRestartsRef.current = 0;
      }

      beginAccessKeyHandoff();
      setHandoff(true);
      void decideRecoveryCodesHandoff(previous, next, abort.signal).then(
        (decision) => finish(generation, decision, previous, next),
        () => {
          if (generation !== handoffGenerationRef.current) return;
          finish(generation, "clear", previous, next);
        },
      );
    };
    const unsubscribe = onAccessKeyChange(reloadIfKeyChanged);
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) reloadIfKeyChanged();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      unsubscribe();
      window.removeEventListener("pageshow", onPageShow);
      handoffGenerationRef.current += 1;
      handoffAbortRef.current?.abort();
      endAccessKeyHandoff();
    };
  }, [queryClient]);

  const login = useCallback(
    (key: string) => {
      handoffGenerationRef.current += 1;
      handoffAbortRef.current?.abort();
      endAccessKeyHandoff();
      setHandoff(false);
      handoffRestartsRef.current = 0;
      setAccessKey(key);
      accessKeyRef.current = key;
      queryClient.clear();
      clearStoredSupportContext();
      clearPersistedRecoveryCodesAtRisk();
      setAccessKeyState(key);
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    if (isAccessKeyHandoff()) return;
    if (getAccessKey()) {
      revokeCurrentSession().catch(() => {});
    }
    cancelAllCeremonies();
    clearAccessKey();
    accessKeyRef.current = null;
    queryClient.clear();
    clearStoredSupportContext();
    clearPersistedRecoveryCodesAtRisk();
    setAccessKeyState(null);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accessKey,
      isLoading,
      isAuthenticated: accessKey !== null,
      login,
      logout,
    }),
    [accessKey, isLoading, login, logout],
  );

  return (
    <>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
      {handoff
        ? createPortal(
            <p
              id={HANDOFF_STATUS_ID}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            />,
            document.body,
          )
        : null}
    </>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
